import React from 'react';
import type { ComponentFieldDefinition } from '@troia-v3/types';
import { FormField } from '@/shared/components/ui/FormField';
import { Select } from '@/shared/components/ui/Select';
import { useFunnelsDropdown } from '@/modules/funnels/hooks/useFunnels';
import { useStepsByFunnelId } from '@/modules/funnels/hooks/useFunnelSteps';
import type { FunnelResponse, FunnelStepResponse } from '@troia-v3/types';

interface FunnelStepCascadeSelectorProps {
  field: ComponentFieldDefinition;
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
}

/**
 * Cascata funil → etapa. Persiste `funnelId` (auxiliar, pré-seleciona o funil ao
 * reabrir) e `field.name` (= funnelStepId, valor lido pelo renderer/endpoint).
 * Usa rotas dropdown sem permissão (corrige NUNCA #15 do seletor antigo).
 */
export const FunnelStepCascadeSelector: React.FC<FunnelStepCascadeSelectorProps> = ({
  field,
  values,
  onChange,
}) => {
  const funnelId = (values['funnelId'] as string) || '';
  const stepId = (values[field.name] as string) || '';

  const { data: funnels } = useFunnelsDropdown();
  const { data: steps } = useStepsByFunnelId(funnelId || undefined);

  const funnelOptions = (funnels ?? []).map((f: FunnelResponse) => ({
    value: f.id,
    label: f.name,
  }));

  const stepOptions = (steps ?? []).map((s: FunnelStepResponse) => ({
    value: s.id,
    label: s.name,
  }));

  const handleFunnel = (nextFunnelId: string) => {
    onChange('funnelId', nextFunnelId);
    onChange(field.name, ''); // reset da etapa ao trocar de funil
  };

  return (
    <FormField
      label={field.label || 'Funil e etapa de destino'}
      description={field.description || 'O lead capturado entra nesta etapa do funil.'}
      required={field.required}
    >
      <div className="space-y-2">
        <Select
          value={funnelId}
          onChange={handleFunnel}
          options={funnelOptions}
          placeholder="Selecione o funil"
          data-testid="funnelstep-funnel-select"
        />
        <Select
          value={stepId}
          onChange={(v) => onChange(field.name, v)}
          options={stepOptions}
          placeholder={funnelId ? 'Selecione a etapa' : 'Escolha o funil primeiro'}
          disabled={!funnelId}
          data-testid="funnelstep-step-select"
        />
      </div>
    </FormField>
  );
};
